import type { ExternalVideo } from './types';

/** No redirects, HTML injection, oEmbed request or remote metadata fetching. */
export function parseVideoUrl(
  value: string,
): Pick<ExternalVideo, 'provider' | 'external_id' | 'url'> | null {
  try {
    const u = new URL(value.trim());
    if (u.protocol !== 'https:' || u.username || u.password || u.port) return null;
    const host = u.hostname.toLowerCase();
    const path = u.pathname.replace(/\/$/, '');
    if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be'].includes(host)) {
      const id =
        host === 'youtu.be'
          ? path.slice(1)
          : path === '/watch'
            ? u.searchParams.get('v')
            : path.match(/^\/(?:shorts|embed)\/([\w-]{11})$/)?.[1];
      if (id && /^[\w-]{11}$/.test(id))
        return {
          provider: 'youtube',
          external_id: id,
          url: `https://www.youtube.com/watch?v=${id}`,
        };
    }
    if (['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(host)) {
      const match = path.match(/^\/(?:video\/)?(\d+)(?:\/([a-fA-F0-9]+))?$/);
      const hash = match?.[2] || u.searchParams.get('h');
      if (match?.[1] && (!hash || /^[a-fA-F0-9]+$/.test(hash)))
        return {
          provider: 'vimeo',
          external_id: match[1],
          url: `https://vimeo.com/${match[1]}${hash ? `/${hash}` : ''}`,
        };
    }
    if (['instagram.com', 'www.instagram.com'].includes(host)) {
      const match = path.match(/^\/(reel|p|tv)\/([\w-]+)$/);
      if (match?.[1] && match[2])
        return {
          provider: 'instagram',
          external_id: match[2],
          url: `https://www.instagram.com/${match[1]}/${match[2]}/`,
        };
    }
    if (['tiktok.com', 'www.tiktok.com'].includes(host)) {
      const match = path.match(/^\/@([\w.]+)\/video\/(\d+)$/);
      if (match?.[1] && match[2])
        return {
          provider: 'tiktok',
          external_id: match[2],
          url: `https://www.tiktok.com/@${match[1]}/video/${match[2]}`,
        };
    }
    if (['facebook.com', 'www.facebook.com', 'm.facebook.com', 'web.facebook.com'].includes(host)) {
      const id = ['/watch', '/video.php'].includes(path)
        ? u.searchParams.get('v')
        : path.match(/^\/(?:reel\/|[\w.-]+\/videos\/)(\d+)$/)?.[1];
      if (id && /^\d+$/.test(id))
        return {
          provider: 'facebook',
          external_id: id,
          url: `https://www.facebook.com/watch/?v=${id}`,
        };
    }
  } catch {
    /* The form reports unsupported URLs without contacting them. */
  }
  return null;
}

export function videoEmbedUrl(video: ExternalVideo): string | null {
  const parsed = parseVideoUrl(video.url);
  if (!parsed || parsed.provider !== video.provider || parsed.external_id !== video.external_id)
    return null;
  const id = encodeURIComponent(parsed.external_id);
  switch (parsed.provider) {
    case 'youtube':
      return `https://www.youtube-nocookie.com/embed/${id}?autoplay=0&playsinline=1`;
    case 'vimeo': {
      const hash = new URL(parsed.url).pathname.split('/')[2];
      return `https://player.vimeo.com/video/${id}?autoplay=0&dnt=1${hash ? `&h=${encodeURIComponent(hash)}` : ''}`;
    }
    case 'facebook':
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(parsed.url)}&show_text=false&autoplay=false`;
    case 'instagram':
      return `${parsed.url}embed/`;
    case 'tiktok':
      return `https://www.tiktok.com/player/v1/${id}?autoplay=0`;
  }
}
